import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  autoMapColumns, errorRowsToCsv, mapRecordsToCodeChanges, parseCodeChangeFile,
} from './bulkCodeImport';
import {
  applyBulkCodeChanges, fetchBulkCodeChangeRun, fetchBulkCodeChangeRuns,
  undoBulkCodeChangeEntry, undoBulkCodeChangeRun, validateBulkCodeChanges,
} from './apiService';

const STATUS_LABELS = {
  ready: 'Ready', auto_resolved: 'Auto-resolved', error: 'Error',
  processed: 'Processed', unvalidated: 'Needs review',
};
const STATUS_COLORS = {
  ready: 'success', auto_resolved: 'info', error: 'error',
  processed: 'success', unvalidated: 'warning',
};
const RUN_STATUS_LABELS = {
  applied: 'Applied', partially_applied: 'Partially applied', rejected: 'Rejected',
  partially_undone: 'Partially undone', undone: 'Undone',
};

const displayPrice = (value) => (
  value === null || value === undefined || value === '' ? '—' : `$${Number(value).toFixed(2)}`
);
const displayDate = (value) => value ? new Date(value).toLocaleString() : '—';

const BulkCodeChangePage = ({ onBack, onProductsChanged, onNotify }) => {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);
  const [oldIndex, setOldIndex] = useState('');
  const [newIndex, setNewIndex] = useState('');
  const [priceIndex, setPriceIndex] = useState('');
  const [rows, setRows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [undoTarget, setUndoTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState('');

  const isReview = rows.length > 0;
  const goodRows = useMemo(() => rows.filter((row) => (
    row.status === 'ready' || row.status === 'auto_resolved'
  )), [rows]);
  const errorRows = useMemo(() => rows.filter((row) => row.status === 'error'), [rows]);
  const processedCount = rows.filter((row) => row.status === 'processed').length;
  const needsReviewCount = rows.filter((row) => row.status === 'unvalidated').length;

  const loadRuns = useCallback(async () => {
    const history = await fetchBulkCodeChangeRuns();
    setRuns(history);
    return history;
  }, []);

  useEffect(() => {
    loadRuns().catch((error) => setPageError(error.message));
  }, [loadRuns]);

  const resetFile = () => {
    setFileName('');
    setParsed(null);
    setOldIndex('');
    setNewIndex('');
    setPriceIndex('');
    setRows([]);
    setPageError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setPageError('');
    try {
      const result = await parseCodeChangeFile(file);
      const mapping = autoMapColumns(result.headers);
      setFileName(file.name);
      setParsed(result);
      setOldIndex(mapping.oldIndex ?? '');
      setNewIndex(mapping.newIndex ?? '');
      setPriceIndex(mapping.priceIndex ?? '');
      setRows([]);
    } catch (error) {
      resetFile();
      setPageError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const validateRows = async (inputRows) => {
    setBusy(true);
    setPageError('');
    try {
      setRows(await validateBulkCodeChanges(inputRows));
    } catch (error) {
      setPageError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleReview = () => validateRows(mapRecordsToCodeChanges(
    parsed.records, Number(oldIndex), Number(newIndex), Number(priceIndex)
  ));

  const handleEdit = (rowId, field, value) => {
    setRows((current) => current.map((row) => row.rowId === rowId ? {
      ...row, [field]: value, status: 'unvalidated', errors: [], resolutions: [],
    } : row));
  };
  const handleRemove = (rowId) => setRows((current) => current.filter((row) => row.rowId !== rowId));
  const rowPayload = (row) => ({
    rowId: row.rowId, sourceRow: row.sourceRow, oldCode: row.oldCode,
    newCode: row.newCode, price: row.price,
  });

  const handleRevalidate = async () => {
    const processedRows = rows.filter((row) => row.status === 'processed');
    const pendingRows = rows.filter((row) => row.status !== 'processed');
    setBusy(true);
    setPageError('');
    try {
      const validated = await validateBulkCodeChanges(pendingRows.map(rowPayload));
      setRows([...processedRows, ...validated].sort((left, right) => left.sourceRow - right.sourceRow));
    } catch (error) {
      setPageError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const viewRun = async (runId) => {
    setBusy(true);
    setPageError('');
    try {
      setSelectedRun(await fetchBulkCodeChangeRun(runId));
    } catch (error) {
      setPageError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleProcess = async () => {
    const submitted = rows.filter((row) => row.status !== 'processed' && row.status !== 'unvalidated');
    setBusy(true);
    setPageError('');
    try {
      const result = await applyBulkCodeChanges(submitted.map(rowPayload), fileName);
      const processedById = new Map(result.processed.map((row) => [row.rowId, row]));
      const rejectedById = new Map(result.rejected.map((row) => [row.rowId, row]));
      setRows((current) => current.map((row) => {
        if (processedById.has(row.rowId)) {
          return { ...row, ...processedById.get(row.rowId), status: 'processed', errors: [], resolutions: [] };
        }
        if (rejectedById.has(row.rowId)) return { ...row, ...rejectedById.get(row.rowId) };
        return row;
      }));
      await loadRuns();
      await viewRun(result.runId);
      if (result.processed.length) {
        await onProductsChanged();
        onNotify(
          `${result.processed.length} product change${result.processed.length === 1 ? '' : 's'} queued for store sync (audit run ${result.runId})`,
          'success'
        );
      }
      if (result.rejected.length) {
        onNotify(`${result.rejected.length} row${result.rejected.length === 1 ? '' : 's'} need more review`, 'warning');
      }
    } catch (error) {
      setPageError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmUndo = async () => {
    if (!undoTarget) return;
    setBusy(true);
    setPageError('');
    try {
      if (undoTarget.entryId) await undoBulkCodeChangeEntry(undoTarget.runId, undoTarget.entryId);
      else await undoBulkCodeChangeRun(undoTarget.runId);
      await onProductsChanged();
      await loadRuns();
      await viewRun(undoTarget.runId);
      onNotify(undoTarget.entryId ? 'Row undone and queued for store sync' : 'Import undone and queued for store sync', 'success');
      setUndoTarget(null);
    } catch (error) {
      setPageError(error.message);
      setUndoTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = () => {
    const blob = new Blob([errorRowsToCsv(errorRows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-code-change-errors.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const mappingIsValid = oldIndex !== '' && newIndex !== '' && priceIndex !== ''
    && new Set([oldIndex, newIndex, priceIndex]).size === 3;

  return (
    <Box className="bulk-code-page">
      <Box className="bulk-code-title-row">
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Back to products</Button>
        <Typography variant="h4" component="h2">Bulk product changes</Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        Every processed row changes the code and price, sets Scale to No, and queues the change for all synced stores.
      </Alert>
      {pageError && <Alert severity="error" sx={{ mb: 2 }}>{pageError}</Alert>}

      {!parsed && (
        <Paper className="bulk-code-upload" variant="outlined">
          <UploadFileIcon color="primary" sx={{ fontSize: 54 }} />
          <Typography variant="h6">Import a product-change file</Typography>
          <Typography color="text.secondary">CSV, XLS, or XLSX with old code, new code, and price columns</Typography>
          <input ref={inputRef} hidden type="file" accept=".csv,.xls,.xlsx" onChange={(event) => handleFile(event.target.files?.[0])} />
          <Button variant="contained" onClick={() => inputRef.current?.click()} disabled={busy}>Choose file</Button>
        </Paper>
      )}

      {parsed && !isReview && (
        <Paper className="bulk-code-mapping" variant="outlined">
          <Typography variant="h6">Map the columns</Typography>
          <Typography color="text.secondary">{fileName} · {parsed.records.length} data row{parsed.records.length === 1 ? '' : 's'}</Typography>
          <Alert severity="info">Column names are matched without regard to capitalization, spaces, underscores, or hyphens. Check all three selections.</Alert>
          <Box className="bulk-code-selects">
            {[
              ['Old code column', oldIndex, setOldIndex],
              ['New code column', newIndex, setNewIndex],
              ['Price column', priceIndex, setPriceIndex],
            ].map(([label, value, setter]) => (
              <FormControl fullWidth required key={label}>
                <InputLabel>{label}</InputLabel>
                <Select
                  label={label}
                  value={value}
                  inputProps={{ 'aria-label': label }}
                  onChange={(event) => setter(event.target.value)}
                >
                  {parsed.headers.map((header, index) => (
                    <MenuItem
                      key={`${label}-${header}-${index}`}
                      value={index}
                      disabled={[oldIndex, newIndex, priceIndex].some((selected) => selected === index && selected !== value)}
                    >{header}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
          <Box className="bulk-code-actions">
            <Button onClick={resetFile}>Choose another file</Button>
            <Button variant="contained" onClick={handleReview} disabled={busy || !mappingIsValid}>Review changes</Button>
          </Box>
        </Paper>
      )}

      {isReview && (
        <>
          <Paper className="bulk-code-summary" variant="outlined">
            <Chip color="success" label={`${goodRows.length} ready`} />
            <Chip color="error" label={`${errorRows.length} errors`} />
            {needsReviewCount > 0 && <Chip color="warning" label={`${needsReviewCount} need revalidation`} />}
            {processedCount > 0 && <Chip color="success" variant="outlined" label={`${processedCount} processed`} />}
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={handleRevalidate} disabled={busy || rows.every((row) => row.status === 'processed')}>Revalidate</Button>
            {errorRows.length > 0 && <Button startIcon={<DownloadIcon />} onClick={downloadErrors}>Download errors</Button>}
            <Button variant="contained" color="warning" onClick={handleProcess} disabled={busy || !goodRows.length || needsReviewCount > 0}>Process good rows</Button>
          </Paper>

          <TableContainer component={Paper} variant="outlined" className="bulk-code-table">
            <Table stickyHeader size="small">
              <TableHead><TableRow><TableCell>File row</TableCell><TableCell>Old code</TableCell><TableCell>New code</TableCell><TableCell>Price</TableCell><TableCell>Scale</TableCell><TableCell>Status and details</TableCell><TableCell align="right">Remove</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.rowId} className={`bulk-code-row bulk-code-row-${row.status}`}>
                    <TableCell>{row.sourceRow}</TableCell>
                    <TableCell><TextField size="small" value={row.oldCode} disabled={row.status === 'processed'} inputProps={{ 'aria-label': `Old code row ${row.sourceRow}` }} onChange={(event) => handleEdit(row.rowId, 'oldCode', event.target.value)} /></TableCell>
                    <TableCell><TextField size="small" value={row.newCode} disabled={row.status === 'processed'} inputProps={{ 'aria-label': `New code row ${row.sourceRow}` }} onChange={(event) => handleEdit(row.rowId, 'newCode', event.target.value)} /></TableCell>
                    <TableCell>
                      <TextField size="small" value={row.price ?? ''} disabled={row.status === 'processed'} inputProps={{ 'aria-label': `Price row ${row.sourceRow}` }} onChange={(event) => handleEdit(row.rowId, 'price', event.target.value)} />
                      {row.currentPrice !== undefined && <Typography variant="caption" display="block">Was {displayPrice(row.currentPrice)}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label="No" color="warning" variant="outlined" />
                      {row.currentScale !== undefined && <Typography variant="caption" display="block">Was {row.currentScale ? 'Yes' : 'No'}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLORS[row.status]} label={STATUS_LABELS[row.status]} />
                      {(row.resolutions || []).map((message) => <Typography key={message} variant="body2" color="info.main">{message}</Typography>)}
                      {(row.errors || []).map((error) => <Typography key={`${error.code}-${error.message}`} variant="body2" color="error">{error.message}</Typography>)}
                    </TableCell>
                    <TableCell align="right"><IconButton aria-label={`Remove row ${row.sourceRow}`} disabled={row.status === 'processed'} onClick={() => handleRemove(row.rowId)}><DeleteOutlineIcon /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box className="bulk-code-bottom-actions"><Button onClick={resetFile} disabled={busy}>Start another import</Button></Box>
        </>
      )}

      <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
        <Typography variant="h6" gutterBottom>Import history and undo</Typography>
        {!runs.length ? <Typography color="text.secondary">No audited imports yet.</Typography> : (
          <TableContainer><Table size="small">
            <TableHead><TableRow><TableCell>Run</TableCell><TableCell>File</TableCell><TableCell>User</TableCell><TableCell>Status</TableCell><TableCell>Results</TableCell><TableCell>Date</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>{runs.map((run) => (
              <TableRow key={run.id}>
                <TableCell>{run.id}</TableCell><TableCell>{run.source_filename}</TableCell><TableCell>{run.actor_name}</TableCell>
                <TableCell>{RUN_STATUS_LABELS[run.status] || run.status}</TableCell><TableCell>{run.processed_count} applied · {run.rejected_count} rejected</TableCell><TableCell>{displayDate(run.created_at)}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => viewRun(run.id)}>View</Button>
                  <Button size="small" color="warning" disabled={!['applied', 'partially_applied', 'partially_undone'].includes(run.status)} onClick={() => setUndoTarget({ runId: run.id, label: `run ${run.id}` })}>Undo run</Button>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></TableContainer>
        )}
      </Paper>

      {selectedRun && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
          <Typography variant="h6">Run {selectedRun.id}: {selectedRun.source_filename}</Typography>
          <Typography color="text.secondary" gutterBottom>{RUN_STATUS_LABELS[selectedRun.status] || selectedRun.status} · {selectedRun.actor_name} · {displayDate(selectedRun.created_at)}</Typography>
          <TableContainer><Table size="small">
            <TableHead><TableRow><TableCell>Row</TableCell><TableCell>Before</TableCell><TableCell>After</TableCell><TableCell>Status</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
            <TableBody>{selectedRun.entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{entry.source_row}</TableCell>
                <TableCell>{entry.before_code || entry.requested_old_code}<br />{displayPrice(entry.before_price)} · Scale {entry.before_scale ? 'Yes' : 'No'}</TableCell>
                <TableCell>{entry.after_code || entry.requested_new_code}<br />{displayPrice(entry.after_price ?? entry.requested_price)} · Scale {entry.after_scale === false ? 'No' : '—'}</TableCell>
                <TableCell>{entry.status}{(entry.errors || []).map((error) => <Typography key={`${entry.id}-${error.code}`} variant="caption" color="error" display="block">{error.message}</Typography>)}</TableCell>
                <TableCell align="right"><Button size="small" color="warning" disabled={entry.status !== 'applied'} onClick={() => setUndoTarget({ runId: selectedRun.id, entryId: entry.id, label: `file row ${entry.source_row}` })}>Undo row</Button></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></TableContainer>
        </Paper>
      )}

      <Dialog open={Boolean(undoTarget)} onClose={() => setUndoTarget(null)}>
        <DialogTitle>Undo {undoTarget?.label}?</DialogTitle>
        <DialogContent><Alert severity="warning">This restores the previous code, price, and Scale value. The reverse code change will be sent to every synced store.</Alert></DialogContent>
        <DialogActions><Button onClick={() => setUndoTarget(null)}>Cancel</Button><Button color="warning" variant="contained" onClick={confirmUndo}>Undo and sync</Button></DialogActions>
      </Dialog>

      {busy && <Box className="bulk-code-busy"><CircularProgress /></Box>}
    </Box>
  );
};

export default BulkCodeChangePage;
